import React, { useEffect, useRef, useState } from 'react';
import { Button, ProgressBar, FileUploaderItem } from '@carbon/react';
import { TrashCan, Upload as UploadIcon } from '@carbon/react/icons';

import { useLazyGetNewsMediaQuery, useDeleteNewsMediaMutation } from '@/redux/new_services/newsMedia';
import { uploadAndRegisterNewsMedia } from './uploadHelper';

// TeaserImagePicker shows the current teaser, lets the admin upload a new
// one (single file, image-only) and polls for conversion status.
//
// Props:
//   - mediaId: number | null
//   - onChange: (newMediaId: number | null) => void
const TeaserImagePicker = ({ mediaId, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [media, setMedia] = useState(null);
  const [error, setError] = useState(null);

  const [loadMedia] = useLazyGetNewsMediaQuery();
  const [deleteMedia] = useDeleteNewsMediaMutation();

  useEffect(() => {
    if (!mediaId) {
      setMedia(null);
      return;
    }
    loadMedia(mediaId).unwrap().then(setMedia).catch(() => setMedia(null));
  }, [mediaId, loadMedia]);

  // Poll while the row is still in "processing".
  useEffect(() => {
    if (!media || media.status === 'ready' || media.status === 'failed') return;
    const handle = setInterval(() => {
      loadMedia(media.model.ID).unwrap().then(setMedia).catch(() => {});
    }, 2000);
    return () => clearInterval(handle);
  }, [media, loadMedia]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const created = await uploadAndRegisterNewsMedia(file, setUploadPct);
      setMedia(created);
      setUploadPct(null);
      onChange?.(created.model.ID);
    } catch (err) {
      console.error('teaser upload failed:', err);
      setError('Upload fehlgeschlagen');
      setUploadPct(null);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!mediaId) return;
    try {
      await deleteMedia(mediaId).unwrap();
    } catch (err) {
      console.warn('delete teaser:', err);
    }
    setMedia(null);
    onChange?.(null);
  };

  if (!mediaId && uploadPct === null) {
    return (
      <div className="teaser-image-picker teaser-image-picker--empty">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <Button kind="tertiary" renderIcon={UploadIcon} onClick={() => fileInputRef.current?.click()}>
          Teaser-Bild auswählen
        </Button>
        {error && <div className="teaser-image-picker__error">{error}</div>}
      </div>
    );
  }

  if (uploadPct !== null) {
    return (
      <div className="teaser-image-picker teaser-image-picker--uploading">
        <ProgressBar label="Hochladen" helperText={`${uploadPct} %`} value={uploadPct} max={100} />
      </div>
    );
  }

  if (media && media.status !== 'ready') {
    return (
      <div className="teaser-image-picker teaser-image-picker--processing">
        <FileUploaderItem
          name={media.name}
          status={media.status === 'failed' ? 'edit' : 'uploading'}
          iconDescription={media.status === 'failed' ? 'Fehler' : 'Wird verarbeitet'}
        />
        {media.status === 'failed' && (
          <Button kind="ghost" size="sm" renderIcon={TrashCan} onClick={handleRemove}>
            Entfernen
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="teaser-image-picker teaser-image-picker--ready">
      <img
        className="teaser-image-picker__preview"
        src={media?.s3_med_url || media?.s3_url}
        alt={media?.name}
      />
      <Button kind="ghost" size="sm" renderIcon={TrashCan} onClick={handleRemove}>
        Entfernen
      </Button>
    </div>
  );
};

export default TeaserImagePicker;
